<template>
  <div class="ai-assistant-container d-flex flex-column fill-height">
    <div class="flex-grow-1 overflow-y-auto chat-messages-container">
      <div 
      v-if="chatMessages.length === 0"
      class="text-center mt-5">
        <span class="font-weight-medium font-rubik mb-1">¿En qué puedo ayudarte?</span>
        <p 
        class="text-caption grey--text">
          Pregunta tus dudas o cualquier cosa que necesites sobre este documento.
        </p>
      </div>

      <!-- Mensajes del chat -->
      <transition-group name="message-fade" tag="div" class="chat-messages pt-4">
        <div v-for="(message, index) in chatMessages" :key="message.id || index" class="chat-message mb-0"
          :class="{ 'user-message': message.sender === 'user', 'ai-message': message.sender === 'ai' }">
          <div class="message-content">
            <p v-if="message.sender === 'user'" class="ma-0 pa-0 message-text-content">{{ message.text }}</p>
            <div v-else-if="message.sender === 'ai' && !message.showLoginCard" v-html="renderMarkdown(message.text)" class="ma-0 markdown-content"></div>
            
            <!-- Mensaje de IA con componente de login -->
            <div v-else-if="message.sender === 'ai' && message.showLoginCard" class="ma-0">
              <div v-html="renderMarkdown(message.text)" class="ma-0 markdown-content mb-3"></div>
              <div
              class="login-card-container"
              style="border: 1px solid #e0e0e0; border-radius: 12px;"
              >
                <LoginCard 
                  :dialog="false"
                  :subtitle="'Inicia sesión para continuar'"
                  :redirect="$route.path"
                  @login-success="handleLoginSuccess"
                />
              </div>
            </div>
            
            <div v-if="message.sender === 'ai' && message.isError" class="text-caption red--text mt-1">
              {{ message.errorText || 'Ocurrió un error' }}
            </div>
            <!-- Action Buttons for AI Messages -->
            <div v-if="message.sender === 'ai' && !message.isStreaming && !message.isError && message.text" 
                 class="ai-message-actions mt-1 d-flex align-center">
                 <v-btn icon small @click="copyToClipboard(message.text)" title="Copiar respuesta">
                   
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-copy-icon lucide-copy"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>

                 </v-btn>
                 <v-btn icon small @click="votePositive(message.id)" title="Me gusta">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-thumbs-up-icon lucide-thumbs-up"><path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/></svg>
                 </v-btn>
                 <v-btn icon small @click="voteNegative(message.id)" title="No me gusta">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-thumbs-down-icon lucide-thumbs-down"><path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z"/></svg>
                 </v-btn>
            </div>
          </div>
        </div>
      </transition-group>

        <!-- Loading Indicator -->
        <div v-if="isAiResponding && chatMessages.length > 0"
             class="d-flex justify-start mt-1 mb-12 ml-6 align-center">
             <v-progress-circular 
             v-if="!receivingResponse"
             indeterminate color="#d3d3d3" size="20"></v-progress-circular>
             <span class="ml-2 font-rubik" style="font-size: 16px;">Analizando documento...</span>
        </div>

      <!-- Placeholder Questions - mostrar solo si no hay mensajes y no está cargando Y NO ES EL PRIMER MISSATGE DE LA IA -->
      <div v-if="chatMessages.length === 0 && !isAiResponding && suggestedQuestions.length > 0" class="question-suggestions mt-2">
           <p class="font-weight-medium mb-2">Ejemplos de preguntas:</p>
           <div class="suggestion-chip-container d-flex flex-wrap justify-center">
               <v-chip
                 v-for="(question, index) in suggestedQuestions"
                 :key="index"
                 outlined
                 class="ma-1 suggestion-chip"
                 @click="askSuggestion(question)"
               >
                 {{ question }}
               </v-chip>
           </div>
       </div>
       <!-- Indicador de càrrega per als suggeriments inicials -->
       <div v-if="chatMessages.length === 0 && isFetchingSuggestions" class="d-flex justify-center my-4">
           <v-progress-circular indeterminate color="grey" size="24"></v-progress-circular>
           <span class="ml-2 grey--text text--darken-1">Buscando sugerencias...</span>
       </div>
    </div>

    <!-- Input Area -->
    <div class="input-area d-flex align-center pa-1 ma-2">
      <v-textarea v-model="chatInput" placeholder="¿Tienes dudas? Pregúntame aquí" rows="1" max-rows="4"
        auto-grow dense hide-details outlined class="flex-grow-1 chat-input"
        @keydown.enter.prevent="handleEnterKey"
        :disabled="isAiResponding"
        ref="chatTextarea"
        style="background-color: white !important;"
        ></v-textarea>
      <v-btn icon color="primary" :class="{ 'pulse-animation': chatInput.trim().length > 0 }"
        @click="sendChatMessage" :disabled="!chatInput.trim() || isAiResponding" 
        class="send-button">
        <v-icon color="white">mdi-arrow-up</v-icon>
      </v-btn>
    </div>
    <div
    v-if="false"
    class="text-center mt-2">
      <a href="#" class="text-caption grey--text text-decoration-none">Compartir feedback</a>
    </div>

    <features-dialog :showDialog.sync="showFeaturesDialog" />

  </div>
</template>

<script>
import { User, Brain } from 'lucide-vue';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import FeaturesDialog from '../../../../components/premium/FeaturesDialog.vue';
import LoginCard from '../../../profile/login/components/LoginCard.vue';
import katex from 'katex';
import 'katex/dist/katex.min.css';


// Simple Unique ID generator for messages (for Vue's :key)
let messageIdCounter = 0;
function generateMessageId() {
  return `msg-${Date.now()}-${messageIdCounter++}`;
}

export default {
    name: 'AIChatAssistant',
    components: {
        LucideUser: User,
        LucideBrain: Brain,
        FeaturesDialog,
        LoginCard
    },
    props: {
        documentId: {
            type: String,
            required: false
        },
        pdfUrls: {
            type: Array,
            required: false,
            default: () => []
        },
        axiosInstance: {
            type: Function,
            required: false
        },
        toastInstance: {
            type: Object,
            required: true
        },
        currentUser: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            chatInput: '',
            chatMessages: [],
            isAiResponding: false,
            receivingResponse: false,
            abortController: null,
            suggestedQuestions: [],
            isFetchingSuggestions: false,
            showFeaturesDialog: false,
            noContextError: false
        };
    },
    mounted() {
        if (this.documentId) {
            this.loadChatHistory();
        }
        document.addEventListener('click', this.handleDocRefClick);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleDocRefClick);
    },
    watch: {
        showFeaturesDialog(isShown) {
            if (!isShown && this.$route.query.premium_from) {
                const query = { ...this.$route.query };
                delete query.premium_from;
                this.$router.push({ query }).catch(err => {
                    if (err.name !== 'NavigationDuplicated') {
                        console.error(err);
                    }
                });
            }
        }
    },
    methods: {
        // Método personalizado para mostrar toasts en posición top-left
        showCustomToast(type, message) {
            this.$toast[type](message, {
                position: 'top-left',
                timeout: 3000,
                closeOnClick: true,
                pauseOnFocusLoss: false,
                pauseOnHover: true,
                draggable: true,
                showCloseButtonOnHover: false,
                hideProgressBar: false,
                closeButton: true,
                icon: true
            });
        },
        async loadChatHistory() {
            try {
                const backendUrl = process.env.VUE_APP_AXIOS_URI;
                const apiUrl = `${backendUrl}/chat/history`;
                
                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: this.$root.token?.value || ''
                    },
                    body: JSON.stringify({
                        documentId: this.documentId,
                        userId: this.currentUser.userId
                    })
                });

                const data = await response.json();
                
                if (response.ok && data.success) {
                    // Convertir el historial al formato de chatMessages
                    this.chatMessages = data.history.map(msg => ({
                        id: generateMessageId(),
                        text: msg.text,
                        sender: msg.sender
                    }));
                    
                    // Desplazar al final del chat si hay mensajes
                    if (this.chatMessages.length > 0) {
                        this.$nextTick(() => {
                            this.scrollToChatBottom();
                        });
                    }
                } else {
                    console.error("Error al cargar el historial del chat:", data.error || response.statusText);
                }
            } catch (error) {
                console.error("Error de red al cargar el historial del chat:", error);
            }
        },
        askSuggestion(question) {
            this.chatInput = question;
            this.sendChatMessage();
        },
        handleEnterKey(event) {
            if (event.shiftKey) return;
            this.sendChatMessage();
        },
        renderMarkdown(rawMarkdown) {
            if (!rawMarkdown) {
                return '';
            }
            
            // Procesar expresiones matemáticas con KaTeX
            let processedMarkdown = this.processInlineMath(rawMarkdown);
            
            // Primero, reemplazar temporalmente los tags doc_ref para evitar que sean procesados por marked
            const tempPlaceholder = "DOC_REF_PLACEHOLDER_";
            const references = [];
            
            // Capturar todas las referencias a páginas - Soporta múltiples páginas separadas por comas
            const refRegex = /<doc_ref>([\d,\s]+)<\/doc_ref>/g;
            let match;
            let refIndex = 0;
            
            while ((match = refRegex.exec(processedMarkdown)) !== null) {
                // Extraer el contenido completo de la referencia (puede contener comas)
                const pageRefs = match[1];
                
                // Obtener la primera referencia (antes de la primera coma, si existe)
                const firstPageNum = pageRefs.split(',')[0].trim();
                
                const placeholder = `${tempPlaceholder}${refIndex}`;
                references.push({ 
                    placeholder, 
                    pageNum: firstPageNum,
                    fullRef: pageRefs // Guardamos la referencia completa para mostrarla en el tooltip (futura implementación)
                });
                
                processedMarkdown = processedMarkdown.replace(match[0], placeholder);
                refIndex++;
            }
            
            // Configurar marked para procesar markdown anidado en tablas
            marked.setOptions({
                gfm: true,        // GitHub Flavored Markdown
                breaks: true,     // Añadir <br> en saltos de línea simples
                headerIds: false, // No añadir IDs a los encabezados
                mangle: false,    // No añadir ID único a encabezados
                tables: true      // Permitir tablas GFM
            });
            
            // Personalizar el renderer para manejar correctamente el markdown en celdas de tabla
            const renderer = new marked.Renderer();
            
            // Guardar el renderer original de celdas
            const originalTableCell = renderer.tablecell;
            
            // Personalizar el renderer de celdas de tabla para procesar markdown anidado
            renderer.tablecell = function(content, flags) {
                // Volver a procesar el contenido de la celda para manejar markdown anidado
                if (content.includes('*') || content.includes('_') || content.includes('`') || 
                    content.includes('-') || content.includes('+') || content.includes('[')) {
                    content = marked(content, { renderer: new marked.Renderer() });
                }
                // Llamar al renderer original con el contenido procesado
                return originalTableCell.call(this, content, flags);
            };
            
            // Convertir Markdown a HTML con marked y el renderer personalizado
            const rawHtml = marked(processedMarkdown, { renderer: renderer });
            
            // Sanejar l'HTML amb DOMPurify
            let cleanHtml = DOMPurify.sanitize(rawHtml, {
                ADD_TAGS: ['doc-ref-chip'],
                ADD_ATTR: ['data-page', 'data-tooltip']
            });
            
            // Añadir clases personalizadas a las tablas generadas
            cleanHtml = cleanHtml.replace(/<table>/g, '<table class="md-table">');
            
            // Ahora reemplazar los placeholders con los chips de referencia
            references.forEach(ref => {
                // Si hay múltiples referencias, añadimos un indicador visual "+" al chip
                const hasMultipleRefs = ref.fullRef.includes(',');
                // Solo mostramos el número de página, el "+" lo añadiremos con CSS si es necesario
                const chipText = ref.pageNum;
                
                // Añadir texto de tooltip como atributo de datos en lugar de title
                const tooltipText = `Desplázate a la página ${ref.pageNum}`;
                const dataTooltip = `data-tooltip="${tooltipText}"`;
                
                // Añadir clase adicional si hay múltiples referencias
                const multipleClass = hasMultipleRefs ? 'multiple-refs' : '';
                
                const chipHtml = `<span class="doc-ref-chip ${multipleClass}" data-page="${ref.pageNum}" ${dataTooltip}>${chipText}</span>`;
                cleanHtml = cleanHtml.replace(ref.placeholder, chipHtml);
            });
            
            return cleanHtml;
        },
        processInlineMath(text) {
            // Expresiones matemáticas en línea: $expresión$
            const inlineMathRegex = /\$([^\$]+)\$/g;
            
            return text.replace(inlineMathRegex, (match, formula) => {
                try {
                    // Renderizar la fórmula con KaTeX
                    const renderedFormula = katex.renderToString(formula, {
                        throwOnError: false,
                        displayMode: false
                    });
                    return renderedFormula;
                } catch (error) {
                    console.error('Error al renderizar fórmula matemática:', error);
                    return match; // Devolver la fórmula original si hay error
                }
            });
        },
        async fetchInitialSuggestions() {
            this.isFetchingSuggestions = true;
            console.log("Fetching initial suggestions...");
            try {
                 const backendUrl = process.env.VUE_APP_AXIOS_URI;
                 const apiUrl = `${backendUrl}/chat/suggestions`;

                 const response = await fetch(apiUrl, {
                     method: 'POST',
                     headers: {
                         'Content-Type': 'application/json',
                         Authorization: this.$root.token?.value || ''
                     },
                     body: JSON.stringify({
                         documentId: this.documentId,
                         userId: this.currentUser.userId
                     })
                 });

                 const data = await response.json();

                 if (response.ok && data.success) {
                     this.suggestedQuestions = data.suggestions;
                     console.log("Suggestions received:", this.suggestedQuestions);
                 } else {
                     console.error("Error fetching suggestions:", data.error || response.statusText);
                     this.suggestedQuestions = [
                        "¿Cuáles son las ideas principales de este documento?",
                        "Explica el concepto de este PDF de forma sencilla",
                        "Resume este documento en 3 puntos clave"
                     ];
                     this.showCustomToast('error', data.error || 'Error al obtener sugerencias.');
                 }
            } catch (error) {
                 console.error("Network error fetching suggestions:", error);
                 this.suggestedQuestions = [
                     "¿Cuáles son las ideas principales de este documento?",
                     "Explica el concepto de este PDF de forma sencilla",
                     "Resume este documento en 3 puntos clave"
                 ];
                 this.showCustomToast('error', 'Error de red al obtener sugerencias.');
            } finally {
                this.isFetchingSuggestions = false;
            }
        },
        async sendChatMessage() {
             if (!this.chatInput.trim() || this.isAiResponding) return;
             await this.sendGenericMessage(this.chatInput, false);
             this.focusTextArea();
        },
        async sendGenericMessage(questionToSend, isWarmup = false) {
            return new Promise(async (resolve, reject) => {
                try {
                    if (!isWarmup && !questionToSend.trim()) {
                        resolve();
                        return;
                    }
                    
                    if (this.isAiResponding) {
                         console.log("La IA ya está respondiendo, abortando la petición anterior...");
                         if (this.abortController) {
                            this.abortController.abort();
                         }
                    }

                    // Verificar si el usuario está logueado
                    if (!isWarmup && (!this.currentUser || !this.currentUser.userId)) {
                        const userMessageText = questionToSend.trim();
                        this.chatInput = '';
                        const userMessage = { id: generateMessageId(), text: userMessageText, sender: 'user' };
                        this.chatMessages.push(userMessage);
                        
                        // Crear mensaje de respuesta de la IA pidiendo login
                        const aiMessage = { 
                            id: generateMessageId(), 
                            text: 'Para poder responder a tus preguntas necesito que inicies sesión. ¡Es gratis y solo te tomará unos segundos!', 
                            sender: 'ai', 
                            isStreaming: false, 
                            isError: false,
                            showLoginCard: true // Flag para mostrar el componente de login
                        };
                        this.chatMessages.push(aiMessage);
                        
                        this.scrollToChatBottom();
                        
                        resolve();
                        return;
                    }

                    if (!this.documentId && (!this.pdfUrls || this.pdfUrls.length === 0)) {
                        this.noContextError = true;
                        this.showCustomToast('error', 'Debes seleccionar un documento o proporcionar URLs de PDF para poder preguntar.');
                        reject(new Error('No document or PDF URLs provided'));
                        return;
                    } else {
                        this.noContextError = false;
                    }

                    if (!isWarmup) {
                        const userMessageText = questionToSend.trim();
                        this.chatInput = '';
                        const userMessage = { id: generateMessageId(), text: userMessageText, sender: 'user' };
                        this.chatMessages.push(userMessage);
                        this.scrollToChatBottom();
                    } else {
                         console.log("Enviando mensaje de calentamiento...");
                    }

                    this.isAiResponding = true;

                    const aiMessage = { id: generateMessageId(), text: '', sender: 'ai', isStreaming: true, isError: false, errorText: '' };
                    this.chatMessages.push(aiMessage);
                    // this.scrollToChatBottom();

                    if (this.abortController) {
                        this.abortController.abort();
                    }
                    this.abortController = new AbortController();

                    try {
                         const backendUrl = process.env.VUE_APP_AXIOS_URI;
                         const apiUrl = `${backendUrl}/chat/ask`;

                         const body = {
                            question: questionToSend,
                            userId: this.currentUser.userId
                         };
                         if (this.documentId) {
                            body.documentId = this.documentId;
                         } else if (this.pdfUrls && this.pdfUrls.length > 0) {
                            body.pdfUrls = this.pdfUrls;
                         }

                         const response = await fetch(apiUrl, {
                             method: 'POST',
                             headers: {
                                 'Content-Type': 'application/json',
                                 'Accept': 'text/event-stream',
                                 Authorization: this.$root.token?.value || ''
                             },
                             body: JSON.stringify(body),
                             signal: this.abortController.signal
                         });

                         if (!response.ok) {
                             let errorMsg = `Error HTTP: ${response.status} ${response.statusText}`;
                             let errorDetails = null;
                             try {
                                const errorBody = await response.json();
                                errorMsg = errorBody.error || errorBody.message || errorMsg;
                                errorDetails = errorBody.details;
                                
                                // Verificar si es un error de límite de preguntas
                                if (errorBody.reason === 'daily_questions_limit' || errorBody.reason === 'monthly_questions_limit') {
                                    // Mostrar el diálogo de características premium
                                    this.openFeaturesDialog('ai_chat_limit_exceeded');
                                }
                             } catch(e) { /* Ignorar */ }

                             console.error("Fetch request failed:", errorMsg, "Details:", errorDetails);
                             this.handleStreamError(aiMessage.id, errorMsg, errorDetails);
                             reject(new Error(errorMsg));
                             return;
                         }

                         if (!response.body) {
                            throw new Error("Response body is null.");
                         }

                         const reader = response.body.getReader();
                         const decoder = new TextDecoder();
                         let buffer = '';

                         while (true) {
                            const { done, value } = await reader.read();
                            if (done) {
                                console.log("Fetch stream finished.");
                                this.handleStreamEnd(aiMessage.id);
                                this.receivingResponse = false;
                                resolve(); // Resolver promesa cuando se completa el stream
                                break;
                            }

                            buffer += decoder.decode(value, { stream: true });

                            let eolIndex;
                            while ((eolIndex = buffer.indexOf('\n')) >= 0) {
                                const line = buffer.substring(0, eolIndex).trim();
                                buffer = buffer.substring(eolIndex + 1);

                                if (line.startsWith('data:')) {
                                    this.receivingResponse = true;
                                    const jsonData = line.substring(5).trim();
                                    try {
                                        const data = JSON.parse(jsonData);
                                        if (data.text) {
                                            const targetMessage = this.chatMessages.find(m => m.id === aiMessage.id);
                                            if (targetMessage && targetMessage.isStreaming) {
                                                targetMessage.text += data.text;
                                                // this.scrollToChatBottom();
                                            } else if (!targetMessage) {
                                                console.warn("Target AI message for streaming not found (ID:", aiMessage.id, ")");
                                                if (this.abortController) this.abortController.abort("Target message removed");
                                                break;
                                            }
                                        }
                                        
                                        // Añadir aviso de límite cercano si está disponible
                                        if (data.questionsLeft !== undefined && data.questionsLeft <= 2 && !this.currentUser.isPremium) {
                                            const warningMessage = data.questionsLeft === 1 
                                                ? 'Te queda solo 1 pregunta más hoy. Actualiza a premium para preguntas ilimitadas.'
                                                : `Te quedan solo ${data.questionsLeft} preguntas más hoy. Actualiza a premium para preguntas ilimitadas.`;
                                            this.showCustomToast('warning', warningMessage);
                                        }
                                    } catch (e) {
                                        console.error("Error parsing SSE data from fetch:", e, "Raw JSON data:", jsonData);
                                    }
                                } else if (line.startsWith('event: error_event')) {
                                    console.warn("Received 'error_event' from backend stream (ignoring, relying on HTTP status or final error)");
                                } else if (line.startsWith('event: end')) {
                                    console.log("Received 'end' event (ignoring, relying on stream 'done')");
                                }
                            }
                             if (this.abortController?.signal.aborted && this.abortController.signal.reason === "Target message removed") {
                                 break;
                             }
                         }

                    } catch (error) {
                         if (error.name === 'AbortError') {
                             console.log("Fetch request aborted. Reason:", error.message);
                              if (error.message !== "Target message removed") {
                                   const targetMessage = this.chatMessages.find(m => m.id === aiMessage.id);
                                    if (targetMessage && targetMessage.isStreaming) {
                                        targetMessage.isStreaming = false;
                                    }
                              }
                              // No rechazar la promesa para abortos intencionales
                              resolve();
                         } else {
                             console.error('Error during fetch stream processing:', error);
                             this.handleStreamError(aiMessage.id,'Error de connexió o processament del stream.');
                             reject(error);
                         }
                    } finally {
                        if (!this.abortController?.signal.aborted) {
                            this.isAiResponding = false;
                        } else if (this.abortController?.signal.reason === "Target message removed") {
                            this.isAiResponding = false;
                        }
                    }
                } catch (outerError) {
                    console.error('Error general en sendGenericMessage:', outerError);
                    this.isAiResponding = false;
                    reject(outerError);
                }
            });
        },
        scrollToChatBottom() {
            this.$nextTick(() => {
                const container = this.$el.querySelector('.chat-messages-container');
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            });
        },
        handleStreamError(messageId, errorMessage, errorDetails = null) {
             const targetMessage = this.chatMessages.find(m => m.id === messageId);
             const displayMessage = errorDetails === 'SAFETY_BLOCK'
                ? 'Lo siento, no puedo responder a eso debido a las políticas de seguridad.'
                : errorDetails === 'daily_questions_limit'
                ? 'Has alcanzado el límite diario de 3 preguntas para usuarios gratuitos. Actualiza a premium para preguntas ilimitadas.'
                : errorDetails === 'monthly_questions_limit'
                ? 'Has alcanzado el límite mensual de 1500 preguntas. El límite se reiniciará el primer día del próximo mes.'
                : errorMessage || 'Ocurrió un error desconocido.';

             if (targetMessage && targetMessage.sender === 'ai') {
                 targetMessage.isError = true;
                 targetMessage.errorText = displayMessage;
                 targetMessage.text = targetMessage.text || '';
                 targetMessage.isStreaming = false;
             } else {
                  console.warn("Target AI message for error not found or not AI (ID:", messageId, ")");
                  if (!this.chatMessages.some(m => m.id === messageId)) {
                     this.chatMessages.push({ id: generateMessageId(), text: '', sender: 'ai', isError: true, errorText: displayMessage });
                  }
             }
             this.showCustomToast('error', displayMessage);
             this.scrollToChatBottom();
        },
         handleStreamEnd(messageId) {
             const targetMessage = this.chatMessages.find(m => m.id === messageId);
             if (targetMessage && targetMessage.sender === 'ai' && targetMessage.isStreaming) {
                 targetMessage.isStreaming = false;
                 if (!targetMessage.text && !targetMessage.isError) {
                     targetMessage.text = "[Respuesta vacía]";
                 }
             } else if (targetMessage && !targetMessage.isStreaming) {
                 
             } else {
                 console.warn("Target AI message for stream end not found (ID:", messageId, ")");
             }
            //  this.scrollToChatBottom();
             this.focusTextArea();
         },
         focusTextArea() {
             this.$nextTick(() => {
                 if (this.$refs.chatTextarea) {
                     this.$refs.chatTextarea.focus();
                 }
             });
         },
        copyToClipboard(text) {
            navigator.clipboard.writeText(text).then(() => {
                this.showCustomToast('success', 'Respuesta copiada al portapapeles');
            }).catch(err => {
                console.error('Error al copiar texto: ', err);
                this.showCustomToast('error', 'Error al copiar la respuesta');
            });
        },
        votePositive(messageId) {
            // Placeholder - futura implementación
            console.log("Voto positivo para:", messageId);
            this.showCustomToast('info', 'Gracias por tu feedback'); 
        },
        voteNegative(messageId) {
            // Placeholder - futura implementación
            console.log("Voto negativo para:", messageId);
            this.showCustomToast('info', 'Gracias por tu feedback'); 
        },
        // Método para manejar clics en los chips de referencia
        handleDocRefClick(event) {
            // Comprobar si el elemento clicado es un chip de referencia o está dentro de uno
            const chipElement = event.target.closest('.doc-ref-chip');
            if (chipElement) {
                const pageNum = chipElement.getAttribute('data-page');
                if (pageNum) {
                    this.navigateToDocPage(parseInt(pageNum, 10));
                }
            }
        },
        // Método para navegar a la página referenciada en el documento
        navigateToDocPage(pageNum) {
            // Emitir un evento personalizado que será escuchado por el componente padre
            this.$emit('navigate-to-page', pageNum);
            // this.toastInstance.info(`Navegando a la página ${pageNum}`);
        },
        openFeaturesDialog(origin) {
            this.$router.push({ query: { ...this.$route.query, premium_from: origin } }).catch(err => {
                if (err.name !== 'NavigationDuplicated') {
                    console.error(err);
                }
            });
            this.showFeaturesDialog = true;
        },
        handleLoginSuccess() {
            // Buscar la última pregunta del usuario antes de eliminar mensajes
            const lastUserMessage = [...this.chatMessages]
                .reverse()
                .find(message => message.sender === 'user');
            
            // Guardar el texto de la pregunta
            const lastQuestion = lastUserMessage ? lastUserMessage.text : null;
            
            // Eliminar el mensaje con el formulario de login
            this.chatMessages = this.chatMessages.filter(message => !message.showLoginCard);
            
            if (lastQuestion) {
                // Enviar la última pregunta del usuario a la IA
                this.sendGenericMessage(lastQuestion, false);
            }
        }
    }
}
</script>

<style>
/* Estils per al contenedor del Asistente IA */
.ai-assistant-container {
    height: 100%; /* Asegurar que ocupe toda la altura del tab-item */
    display: flex;
    flex-direction: column;
    background-color: white;
    overflow-x: hidden; /* Evitar scroll horizontal */
}

/* Ajustes para v-tabs-items (si fuera necesario ajustar desde este componente, pero mejor en el padre) */
/* 
.v-tabs-items {
    height: calc(100% - 48px); 
}
*/

.chat-messages-container {
    flex-grow: 1;
    overflow-y: auto;
    overflow-x: hidden; /* Evitar scroll horizontal */
    padding: 0 4px;
}

.chat-messages {
    padding-bottom: 16px;
    max-width: 100%; /* Asegurar que no exceda el ancho del contenedor */
    overflow-x: hidden; /* Evitar scroll horizontal */
}

.chat-message {
    border-radius: 12px;
    margin-bottom: 1rem; /* mb-4 */
}

.user-message {
    max-width: 75%;
    width: fit-content;
    border-radius: 16px !important;
    margin-left: auto;
    background-color: #F2F2F2;
}

.ai-message {
    margin-right: auto;
    background-color: white !important;
    padding: 0.5rem 0.75rem;
    margin-top: 0.5rem;
    max-width: 550px;
    width: 100%; /* Asegurar que ocupe el ancho disponible hasta max-width */
    word-wrap: break-word; /* Permitir que las palabras se rompan */
    overflow-wrap: break-word; /* Alternativa moderna a word-wrap */
}

.message-content {
    /* background-color: #f0f0f0; */
    border-radius: 12px;
    padding: 0.5rem 1rem; /* pa-3 */
    font-family: "apple-system", "system-ui", "BlinkMacSystemFont", "Segoe UI", "Roboto", "Ubuntu";
    /* Asegurar que el texto sea seleccionable */
    user-select: text;
    -webkit-user-select: text;
    -moz-user-select: text;
    -ms-user-select: text;
}



.ai-message .message-content {
    background-color: white !important; /* Keep transparent for AI or set a light color */
    padding: 0; /* Reset padding if markdown-it-vue handles it */
}

.ai-message .markdown-body {
    padding: 0.75rem; /* Apply padding inside markdown if needed */
    border-radius: 12px; /* Optional: Rounded corners for AI markdown block */
}

/* Animación simple de aparición de mensajes */
.message-fade-enter-active,
.message-fade-leave-active {
    transition: opacity 0.2s ease;
}

.message-fade-enter,
.message-fade-leave-to {
    opacity: 0;
}

.question-suggestions {
    padding: 8px 12px;
    text-align: center;
    margin-top: 0.5rem; /* mt-2 */
}

.suggestion-chip-container {
    /* Contenedor para los chips */
    align-items: stretch; /* O center, flex-start...*/
}

/* Estilos para los chips de sugerencias */
.suggestion-chip {
    margin: 4px;
    border-radius: 24px !important;
    font-size: 0.85rem !important;
    /* padding: 6px 12px !important; */ /* Comentat per ajustar manualment */
    cursor: pointer;
    transition: background-color 0.2s ease;

    /* --- Modificacions per al wrapping --- */
    height: auto !important;          /* Permetre alçada automàtica */
    white-space: normal !important;   /* Permetre wrapping normal del text */
    line-height: 1.3 !important;     /* Ajustar interlineat per llegibilitat */
    text-align: left !important;      /* Alineació del text dins del xip */
    padding-top: 8px !important;      /* Ajustar padding vertical */
    padding-bottom: 8px !important;
    padding-left: 12px !important;    /* Mantenir padding horitzontal */
    padding-right: 12px !important;
    /* Assegurar que el contingut intern també pugui créixer */
    display: flex !important; 
    align-items: center !important;
}

.suggestion-chip:hover {
    background-color: #f5f5f5 !important; /* Usar !important si es necesario para sobrescribir Vuetify */
}

/* Input Area Styles */
.input-area {
    /* d-flex align-center ya gestionado por la clase */
    background-color: transparent;
}

.chat-input >>> .v-input__slot {
    margin-bottom: 0 !important; /* Evitar espacio extra debajo del textarea */
    font-size: 1rem !important;
}

.chat-input >>> textarea {
    font-size: 0.9rem !important;
    font-family: -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu;
}

.send-button {
    transition: all 0.3s ease;
    border-radius: 50%;
    height: 40px;
    width: 40px;
    background-color: #4dd453 !important; /* Usar !important si es necesario */
    margin-left: 0.5rem; /* ml-2 */
}

.send-button:hover:not(:disabled) {
    background-color: #3aa44a !important;
}

/* Animación de pulso para el botón de enviar */
@keyframes pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(77, 212, 83, 0.5);
    }

    70% {
        box-shadow: 0 0 0 8px rgba(77, 212, 83, 0);
    }

    100% {
        box-shadow: 0 0 0 0 rgba(77, 212, 83, 0);
    }
}

.pulse-animation {
    animation: pulse 1.5s infinite;
}

/* Mejoras adicionales para consistencia */
.text-caption {
    font-size: 0.75rem !important;
}

.font-weight-medium {
    font-weight: 500 !important;
}

.mb-1 {
    margin-bottom: 0.25rem !important;
}

.mb-2 {
    margin-bottom: 0.5rem !important;
}

.mt-2 {
    margin-top: 0.5rem !important;
}

.mt-3 {
    margin-top: 1rem !important;
}

.grey--text {
    color: #757575 !important;
}

.red--text {
    color: #f44336 !important;
}

/* Afegir un estil per al cursor durant la resposta */
.ai-message .message-content p {
  /* Potser una animació subtil o res especial */
}

/* Es podria afegir un petit indicador visual al costat del text que s'està generant */
.ai-message[isStreaming="true"] .message-content::after {
  content: '▍'; /* Un cursor simple */
  animation: blink 1s infinite;
  margin-left: 4px;
  vertical-align: baseline;
  opacity: 0.7;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

/* Afegir un estil per al missatge d'error */
.ai-message .text-caption.red--text {
    font-weight: 500;
}

/* Estil per al text del missatge */
.message-text-content {
    white-space: pre-wrap; /* Respectar salts de línia i espais */
    word-wrap: break-word; /* Trencar paraules llargues */
    /* Assegurem que els estils de markdown funcionin dins del div */
    line-height: 1.6;
}

/* Millores d'estil per al markdown generat per marked */
.markdown-content {
  user-select: text !important;
  -webkit-user-select: text !important;
  -moz-user-select: text !important;
  -ms-user-select: text !important;
  font-family: "apple-system", "system-ui", "BlinkMacSystemFont", "Segoe UI", "Roboto", "Ubuntu" !important;
  line-height: 1.5 !important;
  color: #333333 !important;
  font-size: 0.95rem !important;
  max-width: 100% !important; /* Limitar el ancho del contenido */
  overflow-wrap: break-word !important; /* Permitir que las palabras largas se rompan */
}

.markdown-content >>> h1,
.markdown-content >>> h2,
.markdown-content >>> h3,
.markdown-content >>> h4 {
  color: #424242 !important;
  margin-top: 0.7em !important;
  margin-bottom: 0.4em !important;
  font-weight: 500 !important;
  letter-spacing: -0.01em !important;
}

.markdown-content >>> h1 {
  font-size: 1.4em !important;
  border-bottom: 1px solid #eeeeee !important;
  padding-bottom: 0.3em !important;
}

.markdown-content >>> h2 {
  font-size: 1.25em !important;
  border-bottom: 1px solid #eeeeee !important;
  padding-bottom: 0.3em !important;
}

.markdown-content >>> h3 {
  font-size: 1.1em !important;
}

.markdown-content >>> h4 {
  font-size: 1em !important;
}

.markdown-content >>> p {
  margin-bottom: 0.8em !important;
  line-height: 1.5 !important;
  color: #424242 !important;
}

.markdown-content >>> a {
  color: #607d8b !important;
  text-decoration: none !important;
  border-bottom: 1px solid #e0e0e0 !important;
  transition: color 0.2s ease, border-color 0.2s ease !important;
  font-weight: normal !important;
}

.markdown-content >>> a:hover {
  color: #455a64 !important;
  border-color: #607d8b !important;
  text-decoration: none !important;
}

.markdown-content >>> ul,
.markdown-content >>> ol {
  padding-left: 1.5em !important;
  margin-bottom: 0.8em !important;
  color: #424242 !important;
}

.markdown-content >>> li {
  margin-bottom: 0.25em !important;
}

.markdown-content >>> blockquote {
  border-left: 3px solid #e0e0e0 !important;
  padding: 0.2em 0 0.2em 1em !important;
  margin: 0 0 0.8em 0 !important;
  color: #757575 !important;
  font-style: italic !important;
}

.markdown-content >>> code {
  background-color: #f5f5f5 !important;
  padding: 0.2em 0.4em !important;
  border-radius: 3px !important;
  font-size: 85% !important;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace !important;
  color: #546e7a !important;
}

.markdown-content >>> pre {
  background-color: #f8f9fa !important;
  padding: 12px !important;
  border-radius: 4px !important;
  overflow-x: auto !important; /* Mantener scroll horizontal para código */
  white-space: pre !important; /* Cambiar a pre para que el código no se rompa */
  max-width: 100% !important;
  margin-bottom: 0.8em !important;
  border: 1px solid #f0f0f0 !important;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace !important;
  font-size: 85% !important;
}

.markdown-content >>> pre > code {
  background-color: transparent !important;
  padding: 0 !important;
  color: #546e7a !important;
}

.markdown-content >>> hr {
  height: 1px !important;
  padding: 0 !important;
  margin: 16px 0 !important;
  background-color: #eeeeee !important;
  border: 0 !important;
}

/* Añadir un estilo para las listas */
.markdown-content >>> ul li::marker {
  color: #9e9e9e !important;
}

.markdown-content >>> ol li::marker {
  color: #9e9e9e !important;
}

/* Estilos directos para tablas markdown sin usar deep o >>> */
table.md-table {
  border: 1px solid #e0e0e0 !important;
  border-collapse: collapse !important;
  width: 100% !important;
  margin: 12px 0 !important;
  background-color: white !important;
  font-family: "apple-system", "system-ui", "BlinkMacSystemFont", "Segoe UI", "Roboto", "Ubuntu" !important;
  font-size: 0.9em !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important;
  border-radius: 4px !important;
  overflow: hidden !important;
}

table.md-table th, 
table.md-table td {
  border: 1px solid #eeeeee !important;
  padding: 9px 12px !important;
  text-align: left !important;
  transition: background-color 0.2s ease !important;
}

table.md-table th {
  background-color: #fafafa !important;
  color: #616161 !important;
  font-weight: 500 !important;
  font-size: 0.85em !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
}

table.md-table tr:nth-child(even) {
  background-color: #fafafa !important;
}

table.md-table tr:hover {
  background-color: #f5f5f5 !important;
}

/* Mantén el resto de los estilos >>> pero vamos a agregarles !important */
.markdown-content >>> table {
  border-collapse: collapse !important;
  width: 100% !important;
  margin-bottom: 0.8em !important;
  display: block !important;
  overflow-x: auto !important; /* Mantener scroll horizontal para tablas */
  max-width: 100% !important;
  font-size: 0.9em !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 4px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important;
}

.markdown-content >>> table th,
.markdown-content >>> table td {
  padding: 9px 12px !important;
  border: 1px solid #eeeeee !important;
  transition: background-color 0.2s ease !important;
  min-width: 120px !important; /* Ancho mínimo para columnas */
  white-space: normal !important; /* Permitir que el texto se envuelva dentro de las celdas */
}

.markdown-content >>> table tr {
  background-color: #fff !important;
}

.markdown-content >>> table tr:nth-child(2n) {
  background-color: #fafafa !important;
}

.markdown-content >>> table th {
  background-color: #fafafa !important;
  color: #616161 !important;
  font-weight: 500 !important;
  font-size: 0.85em !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
}

/* Estilos para los botones de acción de mensajes IA */
.ai-message-actions {
    /* Ajusta el espacio si es necesario */
}

.ai-message-actions .v-btn {
    color: #9e9e9e; /* Gris */
}

.ai-message-actions .v-btn:hover {
    color: #616161; /* Gris más oscuro */
    background-color: rgba(0, 0, 0, 0.05);
}

/* Estilos para los chips de referencia de documento */
.doc-ref-chip {
    display: inline-flex;
    align-items: center;
    background-color: #e0f2f1;
    color: #00796b;
    border-radius: 10px;
    font-size: 0.65rem;
    padding: 0px 6px !important;
    margin: 0 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-weight: 500;
    position: relative;
    bottom: 1px;
}

.doc-ref-chip:hover {
    outline: 1px solid #b2dfdb;
    transform: translateY(-1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* Estilo para chips con múltiples referencias */
.doc-ref-chip.multiple-refs:after {
    content: "+";
    font-size: 0.65rem;
    font-weight: bold;
    margin-left: 1px;
}

/* Tooltip personalizado */
.doc-ref-chip {
    position: relative;
}

.doc-ref-chip[data-tooltip]:before {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 100%; /* Posicionar encima del chip */
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 8px; /* Espacio entre tooltip y chip */
    padding: 6px 8px;
    background-color: #333333;
    color: white;
    border-radius: 4px;
    font-size: 12px;
    font-weight: normal;
    white-space: nowrap;
    z-index: 1000;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease, transform 0.2s ease;
    transform-origin: bottom center;
    transform: translateX(-50%) scale(0.9);
    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
    pointer-events: none;
}

/* Flecha del tooltip */
.doc-ref-chip[data-tooltip]:after {
    content: '';
    position: absolute;
    bottom: 100%; /* Posicionar justo en la base del tooltip */
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 4px; /* Espacio entre flecha y chip */
    border-width: 4px;
    border-style: solid;
    border-color: #333333 transparent transparent transparent; /* Flecha apuntando hacia abajo */
    z-index: 1000;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease, visibility 0.2s ease;
    pointer-events: none;
}

/* Mostrar tooltip al hacer hover con una pequeña animación */
.doc-ref-chip[data-tooltip]:hover:before {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) scale(1);
}

.doc-ref-chip[data-tooltip]:hover:after {
    opacity: 1;
    visibility: visible;
}

/* .doc-ref-chip::before {
    content: "📌";
    margin-right: 6px;
    font-size: 1em;
} */

/* Contenedor para las tablas con scroll horizontal */
.markdown-content {
  /* ... existing code ... */
  max-width: 100% !important; /* Limitar el ancho del contenido */
  overflow-wrap: break-word !important; /* Permitir que las palabras largas se rompan */
}

/* Contenedor específico para tablas */
.markdown-content .table-container {
  width: 100% !important;
  max-width: 100% !important;
  overflow-x: auto !important;
  display: block !important;
  margin-bottom: 0.8em !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 4px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important;
}

/* Tablas dentro del contenedor */
.markdown-content table {
  width: auto !important; /* Permitir que la tabla tenga su ancho natural */
  min-width: 100% !important; /* Mínimo el ancho del contenedor */
  border-collapse: collapse !important;
  font-size: 0.9em !important;
  margin: 0 !important; /* Sin márgenes dentro del contenedor */
}

/* Estilos específicos para render de tablas con marked */
.markdown-content >>> table {
  margin-bottom: 0.8em !important;
  display: table !important; /* Cambiar a table en lugar de block */
  width: auto !important; /* Permitir que la tabla tenga su ancho natural */
  min-width: 100% !important; /* Al menos tan ancha como su contenedor */
  border: 1px solid #e0e0e0 !important;
  border-radius: 4px !important;
}

/* Añadimos un envoltorio para todas las tablas después de que se rendericen */
.markdown-content a,
.markdown-content pre,
.markdown-content table {
  max-width: 100% !important;
}

/* Aseguramos que el contenedor de la tabla genere scroll */
.markdown-content table {
  display: block !important;
  overflow-x: auto !important;
  white-space: normal !important; /* Cambiamos a normal para que el contenido se envuelva */
}

/* Restauramos el display de los elementos dentro de la tabla */
.markdown-content table th,
.markdown-content table td,
.markdown-content table tr,
.markdown-content table tbody,
.markdown-content table thead {
  display: revert !important;
}

/* Estilos para listas dentro de celdas de tablas */
.markdown-content >>> td ul,
.markdown-content >>> td ol {
  padding-left: 1.5em !important;
  margin-bottom: 0 !important; /* Eliminar margen inferior en listas dentro de celdas */
  text-align: left !important;
  list-style-position: outside !important;
}

.markdown-content >>> td li {
  margin-bottom: 0.25em !important;
  white-space: normal !important; /* Asegurar que las listas se envuelvan correctamente */
  display: list-item !important; /* Forzar visualización como elemento de lista */
}

/* Asegurar que se muestren los bullets en las listas */
.markdown-content >>> td ul {
  list-style-type: disc !important;
}

.markdown-content >>> td ul ul {
  list-style-type: circle !important;
}

.markdown-content >>> td ol {
  list-style-type: decimal !important;
}

/* Ajuste adicional para renderMarkdown */

/* Estilos para KaTeX */
.katex {
    font-size: 1.1em !important;
}

.katex-display {
    margin: 1em 0 !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
}


.input-area {
  outline: 1px solid #E2E2E2 !important;
  border-radius: 13px !important;
}

.input-area >>> textarea {
    color: black !important;
}

.input-area fieldset {
    border: none !important;
}

.input-area >>> textarea::placeholder {
    color: #494949 !important;
}

/* Estilos para el componente de login dentro del mensaje */
.login-card-container {
    max-width: 400px;
    margin: 0 auto;
}

.login-card-container >>> .v-card {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    border-radius: 12px !important;
}

.login-card-container >>> .v-card__title {
    padding-bottom: 8px !important;
}

.login-card-container >>> .v-card__text {
    padding-top: 0 !important;
}
</style> 